import { useMutation } from '@tanstack/react-query';
import { PiArrowClockwiseBold, PiWarningBold } from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { AI_BUTTON, AiDisclaimer, AiError, AiPlateIcon, AiThinking, aiErrorMessage, useAiStatus } from './aiShared';

type Summary = {
    status: 'no_prazo' | 'atencao' | 'critico';
    headline: string;
    summary: string;
    pending: { area?: string; item?: string; why?: string }[];
    next_steps: string[];
    risks: string[];
    generated_at: string;
    cached: boolean;
};

const STATUS = {
    no_prazo: { label: 'No prazo', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' },
    atencao: { label: 'Atenção', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200' },
    critico: { label: 'Crítico', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300' },
};

export default function ShipmentAiSummaryCard({ shipmentId }: { shipmentId: number }) {
    const ai = useAiStatus();
    const gen = useMutation({
        mutationFn: async (refresh: boolean) =>
            (await api.post<{ data: Summary }>(`/ai/shipments/${shipmentId}/summary`, { refresh })).data.data,
    });

    if (!ai.data?.features.shipments) return null;
    const s = gen.data;

    return (
        <section className="overflow-hidden rounded-2xl border border-rose-200 bg-white dark:border-rose-500/30 dark:bg-gray-800">
            <header className="flex items-center gap-2 bg-rose-50/60 px-4 py-3 dark:bg-rose-500/5">
                <AiPlateIcon className="size-5" spinning={gen.isPending} />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Resumo com IA</h3>
                {s && (
                    <button
                        type="button"
                        onClick={() => gen.mutate(true)}
                        disabled={gen.isPending}
                        title="Gerar de novo com os dados atuais"
                        className="ms-auto rounded-md p-1 text-gray-500 hover:bg-white/60 hover:text-gray-900 disabled:opacity-40 dark:hover:bg-gray-700"
                    >
                        <PiArrowClockwiseBold className={cn('size-4', gen.isPending && 'animate-spin')} />
                    </button>
                )}
            </header>
            <div className="p-4">
                {gen.isPending ? (
                    <AiThinking label="Lendo datas, tarefas e eventos…" />
                ) : gen.isError ? (
                    <AiError message={aiErrorMessage(gen.error)} onRetry={() => gen.mutate(false)} />
                ) : !s ? (
                    <div className="space-y-3 text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Situação do processo, o que está pendente por área, próximos passos e riscos — em segundos.
                        </p>
                        <button
                            type="button"
                            onClick={() => gen.mutate(false)}
                            className={cn(AI_BUTTON, 'px-3 py-2 text-xs')}
                        >
                            <AiPlateIcon className="size-4" /> Gerar resumo
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', STATUS[s.status].cls)}>{STATUS[s.status].label}</span>
                            <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{s.headline}</p>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">{s.summary}</p>
                        </div>
                        {s.pending.length > 0 && (
                            <div>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Pendências</p>
                                <ul className="space-y-1.5">
                                    {s.pending.map((p, i) => (
                                        <li key={i} className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-xs dark:bg-gray-900/50">
                                            {p.area && <span className="font-semibold text-rose-700 dark:text-rose-300">{p.area}: </span>}
                                            <span className="text-gray-800 dark:text-gray-100">{p.item}</span>
                                            {p.why && <span className="block text-[11px] text-gray-500"> {p.why}</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {s.next_steps.length > 0 && (
                            <div>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Próximos passos</p>
                                <ol className="list-decimal space-y-1 ps-4 text-xs text-gray-700 dark:text-gray-200">
                                    {s.next_steps.map((n, i) => (
                                        <li key={i}>{n}</li>
                                    ))}
                                </ol>
                            </div>
                        )}
                        {s.risks.length > 0 && (
                            <div>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Riscos</p>
                                <ul className="space-y-1 text-xs text-gray-700 dark:text-gray-200">
                                    {s.risks.map((r, i) => (
                                        <li key={i} className="flex gap-1.5">
                                            <PiWarningBold className="mt-0.5 size-3.5 shrink-0 text-amber-500" />
                                            {r}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <AiDisclaimer generatedAt={s.generated_at} cached={s.cached} />
                    </div>
                )}
            </div>
        </section>
    );
}
